import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';

function Admins() {
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [adminStatus, setAdminStatus] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const listAdmins = httpsCallable(functions, 'listAdmins');
      const result = await listAdmins();
      setAdmins(result.data.admins || []);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleDeleteAdmin = async (targetUid) => {
    if (!window.confirm("¿Seguro que deseas eliminar a este administrador?")) return;
    
    try {
      const deleteAdmin = httpsCallable(functions, 'deleteAdmin');
      await deleteAdmin({ targetUid });
      fetchAdmins();
    } catch (error) {
      console.error("Error deleting admin:", error);
      alert(error.message || "Error al eliminar administrador");
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setAdminStatus(null);

    if (newAdminPassword.length < 6) {
      setAdminStatus({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }

    setAdminLoading(true);
    try {
      const createAdminUser = httpsCallable(functions, 'createAdminUser');
      await createAdminUser({ email: newAdminEmail, password: newAdminPassword });
      
      setAdminStatus({ type: 'success', text: `¡Administrador ${newAdminEmail} creado con éxito!` });
      setNewAdminEmail('');
      setNewAdminPassword('');
      fetchAdmins();
    } catch (error) {
      console.error(error);
      if (error.code === 'functions/already-exists') {
        setAdminStatus({ type: 'error', text: 'El correo ya está en uso por otro usuario.' });
      } else {
        setAdminStatus({ type: 'error', text: 'Ocurrió un error al crear el administrador.' });
      }
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '2rem' }}>
      <div className="page-header" style={{ marginBottom: '2rem' }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>🛡️</span> Administradores
          </h2>
          <p className="page-subtitle">Gestiona los administradores con acceso a la plataforma</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
        
        {/* CREATE ADMIN CARD */}
        <div className="card" style={{ 
          width: '100%',
          maxWidth: '600px',
          padding: '3rem', 
          position: 'relative',
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          borderRadius: '32px',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: 'radial-gradient(circle at top left, rgba(255, 215, 0, 0.08) 0%, transparent 60%)', pointerEvents: 'none', borderRadius: '32px' }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontSize: '1.5rem', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 'bold' }}>Crear Nuevo Administrador</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem', fontSize: '0.95rem' }}>
              Registra un nuevo usuario y concédele permisos de administrador automáticamente.
            </p>

            <form onSubmit={handleCreateAdmin} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label className="form-label" style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="admin@ejemplo.com"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  required
                  style={{ 
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    height: '52px', 
                    fontSize: '1.05rem',
                    borderRadius: '12px',
                    padding: '0 16px',
                    color: '#fff'
                  }}
                />
              </div>

              <div>
                <label className="form-label" style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '8px' }}>
                  Contraseña Inicial
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Mínimo 6 caracteres"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ 
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    height: '52px', 
                    fontSize: '1.05rem',
                    borderRadius: '12px',
                    padding: '0 16px',
                    color: '#fff'
                  }}
                />
              </div>

              {adminStatus && (
                <div className="animate-fade-in" style={{
                  background: adminStatus.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${adminStatus.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  padding: '1rem',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  color: adminStatus.type === 'success' ? '#10b981' : '#ef4444',
                  fontSize: '1rem',
                  fontWeight: 600,
                  marginTop: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{adminStatus.type === 'success' ? '✅' : '⚠️'}</span>
                  {adminStatus.text}
                </div>
              )}

              <button 
                type="submit" 
                className="btn"
                disabled={adminLoading || !newAdminEmail || !newAdminPassword}
                style={{ 
                  marginTop: '1.5rem', 
                  height: '56px', 
                  fontSize: '1.1rem', 
                  fontWeight: 'bold',
                  background: (adminLoading || !newAdminEmail || !newAdminPassword) ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)',
                  color: (adminLoading || !newAdminEmail || !newAdminPassword) ? 'rgba(255,255,255,0.3)' : '#000',
                  border: 'none',
                  borderRadius: '16px',
                  boxShadow: (adminLoading || !newAdminEmail || !newAdminPassword) ? 'none' : '0 8px 20px rgba(255, 215, 0, 0.3)',
                  cursor: (adminLoading || !newAdminEmail || !newAdminPassword) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {adminLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                    <span className="spinner" style={{ width: '20px', height: '20px', borderTopColor: '#000' }} />
                    Creando...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    👤 Crear Administrador
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>

      </div>

      {/* ADMINS LIST CARD */}
      <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
        <div className="card" style={{ 
          width: '100%',
          maxWidth: '1232px',
          padding: '2rem', 
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          borderRadius: '32px'
        }}>
          <h3 style={{ fontSize: '1.5rem', color: '#FFD700', marginBottom: '1.5rem', fontWeight: 'bold' }}>
            Lista de Administradores
          </h3>
          
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Correo</th>
                  <th>ID</th>
                  <th>Fecha de Creación</th>
                  <th>Último Acceso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loadingAdmins ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>Cargando administradores...</td>
                  </tr>
                ) : admins.length > 0 ? (
                  admins.map((admin) => (
                    <tr key={admin.uid}>
                      <td className="font-medium">{admin.email}</td>
                      <td className="mono-text">{admin.uid}</td>
                      <td className="text-sm">{new Date(admin.creationTime).toLocaleDateString()}</td>
                      <td className="text-sm">{new Date(admin.lastSignInTime).toLocaleDateString()}</td>
                      <td>
                        {auth.currentUser?.email === 'admin@mototaxi.com' && admin.email !== 'admin@mototaxi.com' && (
                          <button 
                            className="btn-danger"
                            onClick={() => handleDeleteAdmin(admin.uid)}
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                          >
                            Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      <div className="empty-icon">🛡️</div>
                      <p>No hay administradores adicionales</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admins;
